// apps/web/src/pages/MyProfile/LikeListModal.tsx
import React, { useEffect, useState } from 'react';
import api from '../../../shared/api/axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Liker = {
  _id: string;
  profilename: string;
  picture?: string | null;
};
   
interface LikeListModalProps {
  postId: string;
  onClose: () => void;
}

// Api base URL normalization
const IMAGE_CDN = process.env.REACT_APP_PUBLIC_IMAGE_BASE_URL || '';

const getImageUrl = (path?: string | null) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  return `${IMAGE_CDN.replace(/\/$/, '')}/${path.replace(/^\/+/, '')}`;
};

const LikeListModal: React.FC<LikeListModalProps> = ({ postId, onClose }) => {
  const [likers, setLikers] = useState<Liker[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!postId) return;

    (async () => {
      try {
        setLoading(true);
        const res = await api.get(`/likes/media/${postId}/list`);
        const likedBy: Liker[] = res.data?.likedBy || [];
        setLikers(
          likedBy.map((u) => ({
            ...u,
            picture: u.picture ? getImageUrl(u.picture) : null,

          }))
        );
      } catch (e) {
        console.warn('Failed to load likes list:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [postId]);

  const filtered = likers.filter((u) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (u.profilename || '').toLowerCase().includes(q);
  });

  const handleRowClick = (userId: string) => {
  navigate(`/visitor-profile/${userId}`);
  onClose();
};

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={titleStyle}>{t('likeListModal.title')}</span>
            {likers.length > 0 && (
              <span style={{ color: '#9ca3af', fontSize: 12 }}>
                {likers.length}
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label={t('likeListModal.actions.close')}>
            ×
          </button>
        </div>

        {/* Search */}
        {likers.length > 0 && (
          <div style={searchWrapStyle}>
            <span style={{ color: '#6b7280', marginRight: 6 }}>🔍</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('likeListModal.search.placeholder')}
              style={searchInputStyle}
              aria-label={t('likeListModal.search.ariaLabel')}
            />
          </div>
        )}

        {/* Body */}
        <div style={bodyStyle}>
          {loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px 0',
              }}
            >
              <div className="spinner" />
              <span style={dimTextStyle}>{t('likeListModal.loading')}</span>
            </div>
          ) : likers.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px 0',
              }}
            >
              <span style={emptyTextStyle}>{t('likeListModal.empty.title')}</span>
              <span style={dimTextStyle}>
              {t('likeListModal.empty.subtitle')}
              </span>
            </div>
          ) : filtered.length === 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '32px 0',
              }}
            >
              <span style={emptyTextStyle}>{t('likeListModal.noMatches.title')}</span>
              <span style={dimTextStyle}>{t('likeListModal.noMatches.subtitle')}</span>
            </div>
          ) : (
            filtered.map((item) => (
              <div
                key={item._id}
                style={rowStyle}
                onClick={() => handleRowClick(item._id)}
                onKeyDown={(e) => e.key === 'Enter' && handleRowClick(item._id)}
                tabIndex={0}
              >
                {item.picture ? (
  <img src={item.picture} style={avatarStyle} alt={item.profilename} />
) : (
  <div style={avatarPlaceholderStyle}>👻</div>
)}
                <span style={nameStyle}>{item.profilename || t('likeListModal.unknownUser')}</span>
                <span
                  style={{
                    marginLeft: 'auto',
                    color: '#6b7280',
                    fontSize: 14,
                  }}
                >
                  ›
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};


const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const panelStyle: React.CSSProperties = {
  width: 'min(420px, 90vw)',
  maxHeight: '80vh',
  background: '#020617',
  borderRadius: 16,
  border: '1px solid rgba(148,163,184,0.35)',
  boxShadow: '0 18px 45px rgba(0,0,0,0.6)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '10px 14px',
  borderBottom: '1px solid rgba(30,64,175,0.6)',
};

const titleStyle: React.CSSProperties = {
  color: '#e5e7eb',
  fontSize: 16,
  fontWeight: 700,
};

const closeBtnStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: '#9ca3af',
  cursor: 'pointer',
  padding: 4,
  fontSize: 20,
  lineHeight: 1,
};

const searchWrapStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  margin: '8px 12px 4px',
  padding: '6px 10px',
  borderRadius: 999,
  border: '1px solid rgba(148,163,184,0.4)',
  backgroundColor: 'rgba(15,23,42,0.9)',
};

const searchInputStyle: React.CSSProperties = {
  flex: 1,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: '#e5e7eb',
  fontSize: 14,
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '4px 10px 10px',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  padding: '8px 6px',
  borderRadius: 10,
  cursor: 'pointer',
};

const avatarStyle: React.CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: 8,
  marginRight: 10,
  objectFit: 'cover',
  backgroundColor: 'rgb(38, 29, 162)',
};

const avatarPlaceholderStyle: React.CSSProperties = {
  width: 42,
  height: 42,
  borderRadius: 8,
  marginRight: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgb(38, 29, 162)', // same blue
  fontSize: 22,
  color: '#fff',
};


const nameStyle: React.CSSProperties = {
  color: '#e5e7eb',
  fontSize: 14,
  fontWeight: 600,
  maxWidth: '75%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const dimTextStyle: React.CSSProperties = {
  color: '#9ca3af',
  fontSize: 13,
  textAlign: 'center',
};

const emptyTextStyle: React.CSSProperties = {
  color: '#e5e7eb',
  fontSize: 15,
  fontWeight: 500,
  textAlign: 'center',
};

export default LikeListModal;
